import React, { useState } from 'react';
import {
  Plus, Shield, Edit3, Trash2, FileText, X,
  ArrowLeft, Save, Clock, UploadCloud, FileBadge, ArrowRight, Download, AlertTriangle, Eye, MoreVertical
} from 'lucide-react';
import ReusableTable from '../components/ReusableTable';

const Policy = () => {
  // Views: 'library' (main table), 'detail' (viewing specific policy versions)

  const [openDropdownId, setOpenDropdownId] = useState(null);

  const [currentView, setCurrentView] = useState('library');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [viewingPolicy, setViewingPolicy] = useState(null);
  const [isUploadingNewVersion, setIsUploadingNewVersion] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null); // <-- Added state for delete confirmation




  // Default Mock Data updated to support multiple files and Policy ID
  const [policies, setPolicies] = useState([
    {
      id: '1',
      policyId: 'SEC-001',
      name: 'Access Control Policy',
      description: 'Defines logical access controls to prevent unauthorized access.',
      owner: 'Security Team',
      version: '1.2',
      status: 'Active',
      lastUpdated: 'Jan 2025',
      creationDate: 'Jan 2024',
      files: ['Access_Control_Policy_v1.2.pdf', 'Access_Matrix_Appendix.xlsx'],
      history: [
        { version: '1.2', date: 'Jan 2025', uploadedBy: 'Security Team', files: ['Access_Control_Policy_v1.2.pdf', 'Access_Matrix_Appendix.xlsx'] },
        { version: '1.1', date: 'Jun 2024', uploadedBy: 'Security Team', files: ['Access_Control_Policy_v1.1.pdf'] },
        { version: '1.0', date: 'Jan 2024', uploadedBy: 'Security Team', files: ['Access_Control_Policy_v1.0.pdf'] }
      ]
    },
    {
      id: '2',
      policyId: 'IT-002',
      name: 'Password Policy',
      description: 'Requirements for user password complexity and rotation.',
      owner: 'IT Team',
      version: '1.1',
      status: 'Active',
      lastUpdated: 'Feb 2025',
      creationDate: 'Feb 2024',
      files: ['Password_Policy_v1.1.docx'],
      history: [
        { version: '1.1', date: 'Feb 2025', uploadedBy: 'IT Team', files: ['Password_Policy_v1.1.docx'] },
        { version: '1.0', date: 'Feb 2024', uploadedBy: 'IT Team', files: ['Password_Policy_v1.0.docx'] }
      ]
    }
  ]);

  // --- Pagination Calculations ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const totalPages = Math.ceil(policies.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPolicies = policies.slice(indexOfFirstItem, indexOfLastItem);

  // Form State for New Policy / New Version
  const defaultFormState = {
    policyId: '',
    name: '',
    description: '',
    owner: '',
    creationDate: '',
    version: '1.0',
    files: []
  };
  const [formData, setFormData] = useState(defaultFormState);

  // --- HANDLERS ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFormData(prev => ({ ...prev, files: [...prev.files, ...selectedFiles] }));
    // Reset input value so the same file can be selected again if removed
    e.target.value = '';
  };

  const removeFile = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, index) => index !== indexToRemove)
    }));
  };

  const openNewPolicyModal = () => {
    setFormData(defaultFormState);
    setIsUploadingNewVersion(false);
    setIsUploadModalOpen(true);
  };

  const openNewVersionModal = () => {
    setFormData({
      ...defaultFormState,
      policyId: viewingPolicy.policyId,
      name: viewingPolicy.name,
      description: viewingPolicy.description,
      owner: viewingPolicy.owner,
      version: (parseFloat(viewingPolicy.version) + 0.1).toFixed(1).toString(), // Auto-increment mock
    });
    setIsUploadingNewVersion(true);
    setIsUploadModalOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.version.trim() || !formData.policyId.trim()) return;

    const currentDate = new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    const uploader = formData.owner || 'Admin User';

    // Extract names of the uploaded files, or generate a fallback if none (for mock purposes)
    const uploadedFileNames = formData.files.length > 0
      ? formData.files.map(f => f.name)
      : [`${formData.name.replace(/\s+/g, '_')}_v${formData.version}.pdf`];

    if (isUploadingNewVersion && viewingPolicy) {
      // Logic for adding a new version to an existing policy
      const updatedPolicy = {
        ...viewingPolicy,
        version: formData.version,
        lastUpdated: currentDate,
        files: uploadedFileNames,
        history: [
          { version: formData.version, date: currentDate, uploadedBy: uploader, files: uploadedFileNames },
          ...viewingPolicy.history
        ]
      };

      setPolicies(prev => prev.map(p => p.id === viewingPolicy.id ? updatedPolicy : p));
      setViewingPolicy(updatedPolicy);

    } else {
      // Logic for creating a brand new policy
      const newPolicy = {
        id: Date.now().toString(),
        policyId: formData.policyId,
        name: formData.name,
        description: formData.description,
        owner: formData.owner,
        version: formData.version,
        status: 'Active',
        lastUpdated: currentDate,
        creationDate: formData.creationDate || currentDate,
        files: uploadedFileNames,
        history: [
          { version: formData.version, date: currentDate, uploadedBy: uploader, files: uploadedFileNames }
        ]
      };
      setPolicies([newPolicy, ...policies]);
    }

    setIsUploadModalOpen(false);
  };

  // --- DELETE HANDLERS ---
  const confirmDeletePolicy = (policy, e) => {
    e.stopPropagation();
    setPolicyToDelete(policy);
  };

  const executeDeletePolicy = () => {
    if (!policyToDelete) return;

    setPolicies(prev => prev.filter(p => p.id !== policyToDelete.id));
    if (viewingPolicy?.id === policyToDelete.id) {
      setCurrentView('library');
      setViewingPolicy(null);
    }
    setPolicyToDelete(null); // Close the modal
  };

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const columns = [
    {
      key: 'policyId',
      title: 'Policy ID',
      headerClassName: 'pl-6 w-[100px]',
      cellClassName: 'pl-6',
      render: (policy) => (
        <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs font-bold uppercase tracking-widest rounded-md ring-1 ring-slate-200/60">
          {policy.policyId}
        </span>
      ),
    },
    {
      key: 'name',
      title: 'Policy Details',
      render: (policy) => (
        <div
          className="flex items-center gap-3 cursor-pointer group"
          onClick={() => { setViewingPolicy(policy); setCurrentView('detail'); }}
        >
          <div className="p-2 bg-slate-100 text-slate-500 rounded-lg group-hover:bg-indigo-100 group-hover:text-indigo-600 transition-colors shrink-0">
            <FileText size={18} strokeWidth={2.5} />
          </div>
          <div>
            <span className="text-sm font-bold text-slate-900 block group-hover:text-indigo-600 transition-colors">{policy.name}</span>
            <span className="text-xs font-medium text-slate-500 truncate max-w-xs block mt-0.5">{policy.description}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'owner',
      title: 'Owner',
      render: (policy) => (
        <div className="flex items-center -space-x-2.5">
          <span className="text-sm font-semibold text-slate-700">{policy.owner || '-'}</span>
        </div>
      ),
    },
    {
      key: 'version',
      title: 'Version',
      render: (policy) => (
        <span className="bg-slate-100 text-slate-600 text-xs font-bold px-2.5 py-1 rounded-md ring-1 ring-slate-200">
          v{policy.version}
        </span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (policy) => (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-widest ring-1 shadow-sm shrink-0 whitespace-nowrap ${policy.status === 'Active' ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-amber-50 text-amber-700 ring-amber-200'
          }`}>
          {policy.status}
        </span>
      ),
    },
    {
      key: 'lastUpdated',
      title: 'Last Updated',
      render: (policy) => (
        <span className="text-sm font-medium text-slate-600">{policy.lastUpdated}</span>
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      headerClassName: 'pr-6 text-right',
      cellClassName: 'pr-6 text-right relative',
      // render: (policy) => (
      //   <div className="flex items-center justify-end gap-1">
      //       <button
      //           onClick={() => { setViewingPolicy(policy); setCurrentView('detail'); }}
      //           className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
      //           title="View Policy Details"
      //       >
      //           <Eye size={16} strokeWidth={2.5} />
      //       </button>
      //       <button
      //           onClick={(e) => confirmDeletePolicy(policy, e)}
      //           className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
      //           title="Delete Policy"
      //       >
      //           <Trash2 size={16} strokeWidth={2.5} />
      //       </button>
      //   </div>
      // ),

      render: (policy) => (
        <>
          <button
            onClick={() => setOpenDropdownId(openDropdownId === policy.id ? null : policy.id)}
            className="p-2 bg-white ring-1 ring-transparent hover:ring-slate-200 rounded-xl text-slate-400 hover:text-slate-900 transition-all"
          >
            <MoreVertical className="w-5 h-5" />
          </button>

          {openDropdownId === policy.id && (
            <div className="absolute right-6 bottom-0 sm:top-full mt-2 bg-white/95 backdrop-blur-xl ring-1 ring-slate-200/60 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] z-30 py-2 w-48 text-left animate-in fade-in slide-in-from-top-2" style={{ height: "max-content" }}>

              <button onClick={() => { setViewingPolicy(policy); setCurrentView('detail'); }} className="w-full px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 hover:text-indigo-600 flex items-center gap-3 transition-colors">
                <Eye size={16} /> View
              </button>
              <div className="h-px bg-slate-100 my-1 mx-2" />
              <button onClick={(e) => confirmDeletePolicy(policy, e)} className="w-full px-4 py-2.5 text-sm font-bold text-rose-600 hover:bg-rose-50 flex items-center gap-3 transition-colors">
                <Trash2 size={16} /> Delete Policy
              </button>
            </div>
          )}
        </>
      ),
    }
  ];

  return (
    <div className="max-w-[1400px] mx-auto animate-in fade-in zoom-in-[0.98] duration-400 ease-out">

      {/* --- DELETE CONFIRMATION MODAL --- */}
      {policyToDelete && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity" onClick={() => setPolicyToDelete(null)}></div>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md relative z-10 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 sm:p-8 text-center">
              <div className="w-16 h-16 bg-rose-50 text-rose-600 rounded-full flex items-center justify-center mx-auto mb-5 ring-8 ring-rose-50/50">
                <AlertTriangle size={32} strokeWidth={2} />
              </div>
              <h3 className="text-2xl font-black text-slate-900 mb-2">Delete Policy?</h3>
              <p className="text-sm font-medium text-slate-500 leading-relaxed">
                Are you sure you want to delete <span className="font-bold text-slate-800">"{policyToDelete.name}"</span>? This will permanently remove the policy and all its previous versions.
              </p>
            </div>
            <div className="p-6 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row gap-3">
              <button onClick={() => setPolicyToDelete(null)} className="flex-1 px-4 py-3 text-sm font-bold text-slate-700 bg-white ring-1 ring-slate-200 rounded-xl hover:bg-slate-50 transition-colors shadow-sm">
                Cancel
              </button>
              <button onClick={executeDeletePolicy} className="flex-1 px-4 py-3 text-sm font-bold text-white bg-rose-600 rounded-xl hover:bg-rose-700 shadow-md shadow-rose-600/20 active:scale-95 transition-all">
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- HEADER --- */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div className="flex items-center gap-4">
          {currentView === 'detail' ? (
            <button
              onClick={() => { setCurrentView('library'); setViewingPolicy(null); }}
              className="p-2.5 bg-white ring-1 ring-slate-200 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 hover:ring-indigo-200 rounded-xl transition-all shadow-sm"
            >
              <ArrowLeft size={20} strokeWidth={2.5} />
            </button>
          ) : (
            <div className="p-3 bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-600/20 text-white hidden sm:block">
              <FileBadge size={28} strokeWidth={2} />
            </div>
          )}
          <div>
            <h3 className="text-2xl font-black text-slate-900 tracking-tight">
              {currentView === 'detail' ? viewingPolicy.name : 'Policies'}
            </h3>
            <p className="text-sm font-semibold text-slate-500 mt-0.5">
              {currentView === 'detail' ? 'Manage versions and control evidence mappings.' : 'Upload and manage company policies and compliance documents.'}
            </p>
          </div>
        </div>

        {currentView === 'library' && (
          <button
            onClick={openNewPolicyModal}
            className="flex gap-2 items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all shadow-md active:scale-95"
          >
            <Plus size={18} strokeWidth={3} /> Add Policy
          </button>
        )}
      </div>

      {/* --- MAIN CONTENT --- */}
      {currentView === 'library' ? (

        /* POLICY LIBRARY TABLE */
        <ReusableTable
          columns={columns}
          data={currentPolicies}
          pagination={{
            currentPage,
            totalPages,
            onPageChange: paginate,
            indexOfFirstItem,
            indexOfLastItem,
            totalItems: policies.length
          }}
        />

      ) : (

        /* POLICY DETAIL & VERSION HISTORY VIEW */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-in slide-in-from-right-4 fade-in duration-300">

          {/* Left Column: Metadata */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-6 rounded-3xl ring-1 ring-slate-200/60 shadow-sm flex flex-col gap-6">
              <div>
                <h4 className="text-xs font-black text-slate-400 uppercase tracking-wider mb-1">Description</h4>
                <p className="text-sm font-medium text-slate-700 leading-relaxed">{viewingPolicy.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                <div>
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Policy ID</h4>
                  <p className="text-sm font-bold text-slate-900">{viewingPolicy.policyId}</p>
                </div>
                <div>
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Owner</h4>
                  <p className="text-sm font-bold text-slate-900">{viewingPolicy.owner}</p>
                </div>
                <div>
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Status</h4>
                  <span className="text-xs font-bold text-emerald-600">{viewingPolicy.status}</span>
                </div>
                <div>
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Current Ver</h4>
                  <p className="text-sm font-bold text-indigo-600 bg-indigo-50 inline-block px-2 rounded-md ring-1 ring-indigo-200">v{viewingPolicy.version}</p>
                </div>
                <div className="col-span-2">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Created</h4>
                  <p className="text-sm font-bold text-slate-900">{viewingPolicy.creationDate}</p>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Current Documents</h4>
                  <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-1.5 rounded">{viewingPolicy.files?.length || 0}</span>
                </div>
                <div className="flex flex-col gap-2">
                  {viewingPolicy.files?.map((fileName, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 ring-1 ring-slate-200 rounded-xl group">
                      <div className="flex items-center gap-3 overflow-hidden">
                        <FileText size={18} className="text-slate-400 shrink-0" />
                        <span className="text-sm font-semibold text-slate-700 truncate" title={fileName}>{fileName}</span>
                      </div>
                      <button className="text-slate-400 hover:text-indigo-600 transition-colors shrink-0 ml-2" title="Download">
                        <Download size={16} strokeWidth={2.5} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* <div className="bg-indigo-50 p-6 rounded-3xl ring-1 ring-indigo-100">
                    <h4 className="font-black text-indigo-900 mb-2 flex items-center gap-2"><Shield size={16}/> Control Evidence</h4>
                    <p className="text-xs font-medium text-indigo-700 leading-relaxed mb-4">
                        This policy is stored in the library. To use it as evidence for CC6.1 or other controls, navigate to the <b>Controls</b> dashboard and click "Add Evidence → Select From Policies".
                    </p>
                    <button className="text-sm font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
                        Go to Controls <ArrowRight size={14} />
                    </button>
                </div> */}
          </div>

          {/* Right Column: Version History Table */}
          <div className="lg:col-span-2">
            <div className="bg-white ring-1 ring-slate-200/60 rounded-3xl shadow-sm overflow-hidden flex flex-col h-full">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                <div className="flex items-center gap-2">
                  <Clock size={20} className="text-slate-400" />
                  <h3 className="font-black text-slate-800 tracking-tight">Version History</h3>
                </div>
                <button
                  onClick={openNewVersionModal}
                  className="flex items-center gap-2 text-sm font-bold text-white bg-indigo-600 px-4 py-2 rounded-lg hover:bg-indigo-700 transition-all shadow-md active:scale-95"
                >
                  <UploadCloud size={16} strokeWidth={2.5} /> Upload New Version
                </button>
              </div>

              <div className="p-0 overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-white border-b border-slate-100">
                      <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest w-[15%]">Version</th>
                      <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest w-[20%]">Date</th>
                      <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest w-[35%]">Documents</th>
                      <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest w-[30%] text-right">Uploaded By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {viewingPolicy.history.map((hist, idx) => (
                      <tr key={idx} className="hover:bg-slate-50 transition-colors">
                        <td className="py-4 px-6  flex gap-1 align-center">
                          <span className="text-sm font-black text-slate-700">v{hist.version}</span>
                          {idx === 0 && <span className="ml-2 text-[9px] font-bold bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded uppercase tracking-wider block mt-1 w-max">Latest</span>}
                        </td>
                        <td className="py-4 px-6 text-sm font-semibold text-slate-600 align-top">
                          {hist.date}
                        </td>
                        <td className="py-4 px-6 align-top">
                          <div className="flex flex-col gap-1">
                            {hist.files?.map((f, i) => (
                              <span key={i} className="text-xs font-medium text-slate-600 truncate max-w-[200px]" title={f}>• {f}</span>
                            ))}
                          </div>
                        </td>
                        <td className="py-4 px-6 text-sm font-medium text-slate-800 align-top">
                          <div className="flex items-center gap-2 justify-end">
                            <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600">
                              {hist.uploadedBy.charAt(0)}
                            </div>
                            {hist.uploadedBy}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- UPLOAD FORM MODAL --- */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="absolute inset-0 transition-opacity" onClick={() => setIsUploadModalOpen(false)}></div>
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[95vh] flex flex-col shadow-2xl relative z-10 overflow-hidden animate-in fade-in zoom-in-95 duration-200">

            {/* Modal Header */}
            <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center bg-white z-20">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                  {isUploadingNewVersion ? <UploadCloud size={20} strokeWidth={2.5} /> : <FilePlusIcon />}
                </div>
                <div>
                  <h2 className="text-xl font-black text-slate-900 tracking-tight">
                    {isUploadingNewVersion ? 'Upload New Version' : 'Upload Policy'}
                  </h2>
                  <p className="text-xs font-semibold text-slate-500 mt-0.5">
                    {isUploadingNewVersion ? `Updating ${viewingPolicy?.name}` : 'Add a new compliance document to the library.'}
                  </p>
                </div>
              </div>
              <button onClick={() => setIsUploadModalOpen(false)} className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-full transition-colors">
                <X size={20} strokeWidth={2.5} />
              </button>
            </div>

            {/* Modal Body (Form Fields) */}
            <form id="policyForm" onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-8 space-y-5 bg-slate-50/30 custom-scrollbar">

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="col-span-1 md:col-span-2">
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Policy ID <span className="text-rose-500">*</span></label>
                  <input type="text" name="policyId" value={formData.policyId} onChange={handleInputChange} disabled={isUploadingNewVersion}
                    className="w-full bg-white ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-50 disabled:text-slate-500 transition-all"
                    placeholder="e.g. POL-001" required autoFocus={!isUploadingNewVersion} />
                </div>

                <div className="col-span-1 md:col-span-2">
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Policy Name <span className="text-rose-500">*</span></label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} disabled={isUploadingNewVersion}
                    className="w-full bg-white ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-50 disabled:text-slate-500 transition-all"
                    placeholder="e.g. Access Control Policy" required />
                </div>

                <div className="col-span-1 md:col-span-2">
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Description</label>
                  <textarea name="description" value={formData.description} onChange={handleInputChange} disabled={isUploadingNewVersion}
                    className="w-full bg-white ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-50 disabled:text-slate-500 transition-all resize-none h-20"
                    placeholder="Short explanation of the policy..." />
                </div>

                <div>
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Policy Owner</label>
                  <input type="text" name="owner" value={formData.owner} onChange={handleInputChange} disabled={isUploadingNewVersion}
                    className="w-full bg-white ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-50 disabled:text-slate-500 transition-all"
                    placeholder="e.g. Security Team" />
                </div>

                <div>
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Creation Date</label>
                  <input type="text" name="creationDate" value={formData.creationDate} onChange={handleInputChange} disabled={isUploadingNewVersion}
                    className="w-full bg-white ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-50 disabled:text-slate-500 transition-all"
                    placeholder="e.g. Jan 2024" />
                </div>

                <div className="col-span-1 md:col-span-2 border-t border-slate-100 pt-5 mt-2">
                  <label className="block text-xs font-black text-indigo-500 uppercase tracking-wider mb-2">Version <span className="text-rose-500">*</span></label>
                  <input type="text" name="version" value={formData.version} onChange={handleInputChange}
                    className="w-full bg-indigo-50 ring-2 ring-indigo-200 rounded-xl px-4 py-3 text-sm font-bold text-indigo-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                    placeholder="e.g. 1.0" required />
                  {isUploadingNewVersion && <p className="text-xs text-indigo-600 mt-2 font-medium">Incrementing from previous version v{viewingPolicy.version}.</p>}
                </div>

                <div className="col-span-1 md:col-span-2">
                  <div className="flex justify-between items-end mb-2">
                    <label className="block text-xs font-black text-slate-500 uppercase tracking-wider">Upload Documents <span className="text-rose-500">*</span></label>
                    <span className="text-xs font-bold text-slate-400">{formData.files.length} selected</span>
                  </div>

                  {/* File Upload Dropzone */}
                  <div className="relative border-2 border-dashed border-slate-300 rounded-2xl p-6 hover:bg-slate-50 hover:border-indigo-400 transition-all group flex flex-col items-center justify-center cursor-pointer overflow-hidden bg-white min-h-[140px]">
                    <input type="file" multiple onChange={handleFileChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" required={!isUploadingNewVersion && formData.files.length === 0} />

                    {formData.files.length > 0 ? (
                      <div className="w-full z-20 relative grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {formData.files.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-2.5 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200/50 rounded-xl">
                            <div className="flex items-center gap-2 overflow-hidden pr-2">
                              <FileText size={14} className="shrink-0 text-indigo-400" />
                              <span className="text-xs font-bold truncate">{file.name}</span>
                            </div>
                            <button type="button" onClick={(e) => { e.preventDefault(); removeFile(index); }} className="p-1 hover:bg-indigo-100 hover:text-indigo-900 rounded shrink-0 transition-colors">
                              <X size={14} strokeWidth={3} />
                            </button>
                          </div>
                        ))}
                        {/* Option to add more files visually remains part of the dropzone */}
                        <div className="flex items-center justify-center p-2.5 bg-slate-50 text-slate-500 ring-1 ring-slate-200 border border-dashed border-slate-300 rounded-xl">
                          <Plus size={14} className="mr-1" />
                          <span className="text-xs font-bold pointer-events-none">Add More</span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center pointer-events-none">
                        <UploadCloud size={32} className="mb-3 mx-auto text-slate-300 group-hover:text-indigo-400 transition-colors" strokeWidth={1.5} />
                        <p className="text-sm font-bold text-slate-700 mb-1">Click to upload or drag and drop</p>
                        <p className="text-xs font-medium text-slate-500">PDF, DOCX, XLSX (Multiple allowed)</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </form>

            {/* Modal Footer */}
            <div className="px-8 py-6 border-t border-slate-100 bg-white flex justify-end gap-3 z-20">
              <button type="button" onClick={() => setIsUploadModalOpen(false)} className="px-6 py-2.5 text-sm font-bold text-slate-600 bg-white ring-1 ring-slate-200 rounded-xl hover:bg-slate-50 transition-all shadow-sm">
                Cancel
              </button>
              <button type="submit" form="policyForm" disabled={!formData.name.trim() || !formData.version.trim() || !formData.policyId.trim()} className="px-8 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-xl shadow-md hover:bg-indigo-700 active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                <Save size={16} strokeWidth={2.5} /> Save Policy
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

// Quick helper component for the icon since FilePlus doesn't exist natively in all Lucide versions
const FilePlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="12" y1="18" x2="12" y2="12"></line>
    <line x1="9" y1="15" x2="15" y2="15"></line>
  </svg>
)

export default Policy;